import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";

// Eager: Landing (home + AuthCallback path). Every route the mobile
// user could deep-link into gets lazy-loaded so the initial JS bundle
// shrinks dramatically — this is the single biggest LCP win on
// content pages (Nakshatra, Blog, Angel Numbers, city pages).
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import PaidRoute from "./components/PaidRoute";

const Reports = lazy(() => import("./pages/Reports"));
const ReportDetail = lazy(() => import("./pages/ReportDetail"));
const ReportCheckout = lazy(() => import("./pages/ReportCheckout"));
const ReportView = lazy(() => import("./pages/ReportView"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const ZodiacSign = lazy(() => import("./pages/ZodiacSign"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PlanetPage = lazy(() => import("./pages/PlanetPage"));
const HousePage = lazy(() => import("./pages/HousePage"));
const NakshatraPage = lazy(() => import("./pages/NakshatraPage"));
const KundliCalculator = lazy(() => import("./pages/KundliCalculator"));
const KundliView = lazy(() => import("./pages/KundliView"));
const PredictionsOverview = lazy(() => import("./pages/PredictionsOverview"));
const Predictions = lazy(() => import("./pages/Predictions"));
const TransitToday = lazy(() => import("./pages/TransitToday"));
const AngelNumberPage = lazy(() => import("./pages/AngelNumberPage"));
const AngelNumbersIndex = lazy(() => import("./pages/AngelNumbersIndex"));
const AstrologerCityPage = lazy(() => import("./pages/AstrologerCityPage"));
const AstrologerIndex = lazy(() => import("./pages/AstrologerIndex"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
const CompatibilityIndex = lazy(() => import("./pages/CompatibilityIndex"));

// Admin routes are only reached by one user (sarthakele@gmail.com) so
// they should never sit in the customer bundle.
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./admin/AdminOrderDetail"));
const AdminCustomers = lazy(() => import("./admin/AdminCustomers"));
const AdminCustomerDetail = lazy(() => import("./admin/AdminCustomerDetail"));
const AdminReports = lazy(() => import("./admin/AdminReports"));
const AdminPayments = lazy(() => import("./admin/AdminPayments"));
const AdminQueue = lazy(() => import("./admin/AdminQueue"));
const AdminBlog = lazy(() => import("./admin/AdminBlog"));
const AdminContact = lazy(() => import("./admin/AdminContact"));
const AdminCoupons = lazy(() => import("./admin/AdminCoupons"));
const AdminSettings = lazy(() => import("./admin/AdminSettings"));

function RouteFallback() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#B8860B]" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/reports/:id" element={<ReportDetail />} />
      <Route path="/reports/:id/checkout" element={<ReportCheckout />} />
      <Route path="/reports/view/:id" element={<ReportView />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/horoscope/:sign" element={<ZodiacSign period="daily" />} />
      <Route path="/horoscope/:sign/weekly" element={<ZodiacSign period="weekly" />} />
      <Route path="/horoscope/:sign/monthly" element={<ZodiacSign period="monthly" />} />
      <Route path="/hi/horoscope/:sign" element={<ZodiacSign period="daily" lang="hi" />} />
      <Route path="/hi/horoscope/:sign/weekly" element={<ZodiacSign period="weekly" lang="hi" />} />
      <Route path="/hi/horoscope/:sign/monthly" element={<ZodiacSign period="monthly" lang="hi" />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/planets/:slug" element={<PlanetPage />} />
      <Route path="/houses/:slug" element={<HousePage />} />
      <Route path="/nakshatra/:slug" element={<NakshatraPage />} />
      <Route path="/kundli/calculator" element={<PaidRoute><KundliCalculator /></PaidRoute>} />
      <Route path="/kundli/:id" element={<PaidRoute><KundliView /></PaidRoute>} />
      <Route path="/predictions" element={<PaidRoute><PredictionsOverview /></PaidRoute>} />
      <Route path="/predictions/:id" element={<PaidRoute><Predictions /></PaidRoute>} />
      <Route path="/predictions/:id/:section" element={<PaidRoute><Predictions /></PaidRoute>} />
      <Route path="/transits/today" element={<PaidRoute><TransitToday /></PaidRoute>} />
      <Route path="/angel-numbers" element={<AngelNumbersIndex />} />
      <Route path="/angel-numbers/:slug" element={<AngelNumberPage />} />
      <Route path="/astrologer" element={<AstrologerIndex />} />
      <Route path="/astrologer/:slug" element={<AstrologerCityPage />} />
      {/* Hyphen-form routes match the URLs already ranking in GSC
         (astrologer-delhi & astrologer-hyderabad at positions 1-2). */}
      <Route path="/astrologer-delhi" element={<AstrologerCityPage citySlug="delhi" />} />
      <Route path="/astrologer-mumbai" element={<AstrologerCityPage citySlug="mumbai" />} />
      <Route path="/astrologer-bangalore" element={<AstrologerCityPage citySlug="bangalore" />} />
      <Route path="/astrologer-hyderabad" element={<AstrologerCityPage citySlug="hyderabad" />} />
      <Route path="/astrologer-chennai" element={<AstrologerCityPage citySlug="chennai" />} />
      <Route path="/astrologer-kolkata" element={<AstrologerCityPage citySlug="kolkata" />} />
      <Route path="/astrologer-pune" element={<AstrologerCityPage citySlug="pune" />} />
      <Route path="/astrologer-ahmedabad" element={<AstrologerCityPage citySlug="ahmedabad" />} />
      <Route path="/astrologer-jaipur" element={<AstrologerCityPage citySlug="jaipur" />} />
      <Route path="/astrologer-lucknow" element={<AstrologerCityPage citySlug="lucknow" />} />
      <Route path="/compatibility" element={<CompatibilityIndex />} />
      <Route path="/compatibility/:pair" element={<CompatibilityPage />} />

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="orders/:id" element={<AdminOrderDetail />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="customers/:id" element={<AdminCustomerDetail />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="queue" element={<AdminQueue />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="contact" element={<AdminContact />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Calculators redirect to premium reports */}
      <Route path="/tools" element={<Navigate to="/reports" replace />} />
      <Route path="/tools/*" element={<Navigate to="/reports" replace />} />
      <Route path="/calculators" element={<Navigate to="/reports" replace />} />
      <Route path="/calculators/*" element={<Navigate to="/reports" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster
            position="top-center"
            toastOptions={{
              style: { background: "#FFFFFF", border: "1px solid rgba(30,27,122,0.15)", color: "#1E1B4B", boxShadow: "0 8px 24px -8px rgba(30,27,122,0.15)" },
            }}
          />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
